import React, { useState, useEffect, useMemo, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import { submitContentQuizApi, regenerateContentQuizApi } from "../helper/apicalls/subtopic";
import MCQ from "./MCQ";
import TrueFalse from "./TrueFalse";
import Fillups from "./Fillups";
import Match from "./Match";
import GlobalLoader from "./GlobalLoader";
import AiEvalBreakdownPopup from "./common/AiEvalBreakdownPopup";
import "../styles/ContentQuizPopup.scss";

/**
 * ContentQuizPopup — Interactive popup for content-quiz with MCQs, True/False, Fillups, and Match questions.
 */
const ContentQuizPopup = ({
  isOpen,
  onClose,
  questions = [],
  viewMode = false,
  setQuizViewMode,
  pageKey,
  activeId,
  pageTitle = "subtopic",
  refreshContentQuizData,
  contentQuizData,
  onQuizSubmitSuccess,
  aiEvalData,
  isAiEvalEnabled,
  isAdmin,
  refreshQuizEval,
  pollQuizEval,
  onRegenerateQuizEval,
}) => {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState({}); // Stores user answers
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("questions");
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);
  const [showFeedbackBox, setShowFeedbackBox] = useState(true);
  const [submitResult, setSubmitResult] = useState(null); 
  const [isEvalBreakdownOpen, setIsEvalBreakdownOpen] = useState(false);
  const [quizRegenerating, setQuizRegenerating] = useState(false);
  const [isEvaluatingEval, setIsEvaluatingEval] = useState(false);
  const [forceAttemptMode, setForceAttemptMode] = useState(false);
  const prevAiEvalRef = useRef(aiEvalData);

  const effectiveViewMode = forceAttemptMode ? false : viewMode;

  useEffect(() => {
    if (isOpen) {
      setForceAttemptMode(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isEvaluatingEval && aiEvalData && aiEvalData !== prevAiEvalRef.current) {
      setIsEvaluatingEval(false);
    }
    prevAiEvalRef.current = aiEvalData;
  }, [aiEvalData, isEvaluatingEval]);

  useEffect(() => {
    let timer;
    if (isEvaluatingEval) {
      timer = setTimeout(() => {
        setIsEvaluatingEval(false);
      }, 25000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isEvaluatingEval]);

  const handleRegenerateQuiz = async () => {
    if (quizRegenerating) return;
    try {
      setQuizRegenerating(true);
      setIsEvaluatingEval(true);
      const res = await regenerateContentQuizApi(pageTitle, pageKey, activeId);
      if (refreshContentQuizData) {
        await refreshContentQuizData(activeId);
      }
      if (setQuizViewMode) {
        setQuizViewMode(false);
      }
      setForceAttemptMode(true);
      setSelected({});
      setCurrent(0);
      setSubmitted(false);
      setSubmitResult(null);
      setActiveTab("questions");
      if (refreshQuizEval) {
        refreshQuizEval(true);
      }
      const targetQuizId = activeId || contentQuizData?.[0]?.id;
      if (pollQuizEval && targetQuizId) {
        pollQuizEval(targetQuizId);
      }
    } catch (err) {
      console.error("Error regenerating personalized quiz:", err);
      setIsEvaluatingEval(false);
    } finally {
      setQuizRegenerating(false);
    }
  };





  const parsedEval = useMemo(() => {
    if (!aiEvalData) return { overall: null, scores: {} };
    let record = aiEvalData;
    if (typeof record === "string") {
      try { record = JSON.parse(record); } catch (e) {
        try {
          const validJson = record.replace(/'/g, '"');
          record = JSON.parse(validJson);
        } catch (e2) {
          return { overall: null, scores: {} };
        }
      }
    }
    if (record && record.data) {
      if (typeof record.data === "string") {
        try { record = JSON.parse(record.data); } catch (e) {
          try {
            const validJson = record.data.replace(/'/g, '"');
            record = JSON.parse(validJson);
          } catch (e2) {}
        }
      } else {
        record = record.data;
      }
    }
    if (!record || typeof record !== "object") return { overall: null, scores: {} };
    let overall = record.overall_score ?? record.overallScore ?? null;
    if (overall !== null) {
      const parsedNum = parseFloat(overall);
      if (!isNaN(parsedNum)) overall = parsedNum;
      else overall = null;
    }
    let scores = record.evalution_score ?? record.evalutionScore ?? record.evaluation_score ?? record.evaluationScore ?? {};
    if (typeof scores === "string") {
      try { scores = JSON.parse(scores); } catch (e) {
        try {
          const validJson = scores.replace(/'/g, '"');
          scores = JSON.parse(validJson);
        } catch (e2) { scores = {}; }
      }
    }
    return { overall, scores: typeof scores === "object" && scores !== null ? scores : {} };
  }, [aiEvalData]);

  const quizInfo = contentQuizData?.[0] || contentQuizData || {};

  let feedbackList = [];
  if (Array.isArray(quizInfo.feedback)) {
    feedbackList = quizInfo.feedback;
  } else if (quizInfo.feedback && typeof quizInfo.feedback === "object") {
    feedbackList = [quizInfo.feedback];
  }

  let historyList = [];
  if (Array.isArray(quizInfo.question_attempt_history)) {
    historyList = quizInfo.question_attempt_history;
  } else if (Array.isArray(quizInfo.history)) {
    historyList = quizInfo.history;
  } else if (Array.isArray(quizInfo.attempts)) {
    historyList = quizInfo.attempts;
  }

  // Use actual history length as the source of truth for attempt count,
  // since quizInfo.no_of_attempts may not be updated correctly
  const noOfAttempts = historyList.length || quizInfo.no_of_attempts || 0;

  // Sort history attempts by date descending (latest first).
  // Reverse first since API returns chronological order — if date parsing fails,
  // the reverse order (latest first) is preserved as the safe default.
  const sortedHistory = [...historyList].reverse().sort((a, b) => {
    const dateA = new Date(
      a.attempted_at || a.created_at || a.last_attempted_at || 0,
    );
    const dateB = new Date(
      b.attempted_at || b.created_at || b.last_attempted_at || 0,
    );
    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    if (isNaN(timeA) || isNaN(timeB)) return 0;
    return timeB - timeA;
  });

  const hasHistory = sortedHistory.length > 0;

  // Selected attempt details
  const currentAttemptObj = hasHistory
    ? sortedHistory[selectedAttemptIndex]
    : quizInfo;

  const getQuestionsFromAttempt = (attemptObj) => {
    if (!attemptObj) return null;
    const attemptQuestions = attemptObj.question ?? attemptObj.questions;
    return Array.isArray(attemptQuestions) && attemptQuestions.length > 0
      ? attemptQuestions
      : null;
  };

  // Preprocess the questions array to flatten any sub-questions (e.g. MCQ, TrueFalse, Fillups groups)
  const parsedQuestionsList = useMemo(() => {
    let sourceQuestions = questions;
    // In View Answers mode, always use questions from the selected question_attempt_history entry
    if (effectiveViewMode) {
      const attemptQuestions = getQuestionsFromAttempt(currentAttemptObj);
      if (attemptQuestions) {
        sourceQuestions = attemptQuestions;
      }
    } else if (submitted) {
      const attemptQuestions = getQuestionsFromAttempt(currentAttemptObj);
      if (attemptQuestions) {
        sourceQuestions = attemptQuestions;
      }
    }


    if (!sourceQuestions) return [];
    const list = [];
    sourceQuestions.forEach((qItem) => {
      let content = qItem.content || qItem;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {}

      const type = (qItem.type || "").toLowerCase().trim();

      // If it has multiple sub-questions inside content.questions
      if (
        (type === "mcq" ||
          type === "quiz" ||
          type === "trueorfalse" ||
          type === "truefalse" ||
          type === "fillups") &&
        content?.questions &&
        Array.isArray(content.questions)
      ) {
        // Find if there is an explanation-only item
        const explanationItemIndex = content.questions.findIndex(
          (subQ) => subQ.explanation && !subQ.question && !subQ.key,
        );
        let explanation = "";
        let filteredQuestions = content.questions;
        if (explanationItemIndex !== -1) {
          explanation = content.questions[explanationItemIndex].explanation;
          filteredQuestions = content.questions.filter(
            (_, i) => i !== explanationItemIndex,
          );
        }

        filteredQuestions.forEach((subQ, idx) => {
          list.push({
            ...qItem,
            subQuestionIndex: idx,
            individualQuestion: {
              ...subQ,
              explanation:
                subQ.explanation || explanation || content.explanation || "",
            },
            type: type,
            // Construct a stable and unique ID for the sub-question
            question_id:
              subQ.question_id ||
              subQ.id ||
              subQ.question ||
              `${qItem.id || qItem._id || qItem.sequence || "q"}_${idx}`,
          });
        });
      } else {
        list.push(qItem);
      }
    });
    return list;
  }, [questions, effectiveViewMode, submitted, currentAttemptObj]);


  const totalQ = parsedQuestionsList ? parsedQuestionsList.length : 0;

  // Helper to extract question ID
  const getQuestionId = (qItem) => {
    if (!qItem) return "";
    if (qItem.individualQuestion) {
      return (
        qItem.individualQuestion.question_id ||
        qItem.individualQuestion.id ||
        qItem.individualQuestion.question ||
        qItem.question_id
      );
    }

    let content = qItem.content || qItem;
    try {
      if (typeof content === "string") content = JSON.parse(content);
      if (typeof content === "string") content = JSON.parse(content);
    } catch (e) {}

    const type = (qItem.type || "").toLowerCase().trim();
    const questionObj =
      type === "match" ? content : content?.questions?.[0] || content;

    return (
      content?.question_id ||
      content?.id ||
      qItem.sequence ||
      questionObj?.question_id ||
      questionObj?.id ||
      questionObj?.question
    );
  };

  // Helper to extract question text
  const getQuestionText = (qItem) => {
    if (!qItem) return "";
    if (qItem.individualQuestion) {
      return qItem.individualQuestion.question || qItem.question || "";
    }

    let content = qItem.content || qItem;
    try {
      if (typeof content === "string") content = JSON.parse(content);
      if (typeof content === "string") content = JSON.parse(content);
    } catch (e) {}

    const type = (qItem.type || "").toLowerCase().trim();
    const questionObj =
      type === "match" ? content : content?.questions?.[0] || content;
    return questionObj?.question || qItem.question || "";
  };

  const isAllQuestionsAnswered = useMemo(() => {
    if (!parsedQuestionsList || parsedQuestionsList.length === 0) return false;
    return parsedQuestionsList.every((qItem) => {
      const questionId = getQuestionId(qItem);
      const val = selected[questionId];
      if (val === undefined || val === null) return false;

      const type = (qItem.type || "").toLowerCase().trim();
      if (type === "fillups") {
        const fillValue =
          val && typeof val === "object" && "value" in val
            ? val.value
            : val;
        return (
          fillValue !== undefined &&
          fillValue !== null &&
          String(fillValue).trim() !== ""
        );
      }

      if (type === "match") {
        const matchValue =
          val && typeof val === "object" && "value" in val
            ? val.value
            : val;
        if (Array.isArray(matchValue)) {
          return matchValue.length > 0;
        }
        return (
          matchValue !== undefined &&
          matchValue !== null &&
          String(matchValue).trim() !== ""
        );
      }

      return String(val).trim() !== "";
    });
  }, [parsedQuestionsList, selected]);

  const currentAttemptScore =
    currentAttemptObj?.no_of_question_correct !== undefined &&
    currentAttemptObj?.no_of_question_correct !== null
      ? currentAttemptObj.no_of_question_correct
      : currentAttemptObj?.score !== undefined &&
          currentAttemptObj?.score !== null
        ? currentAttemptObj.score
        : null;

  const currentAttemptTotal =
    currentAttemptObj?.total_questions ||
    currentAttemptObj?.no_of_questions_attempted ||
    totalQ;

  /**
   * Helper to load user_answers from a history attempt object into the `selected` state map.
   * Returns the populated answers object.
   */
  const loadAnswersFromHistory = (historyObj) => {
    if (!historyObj) return {};
    const previousAnswers = historyObj.user_answers || [];
    if (previousAnswers.length === 0) return {};

    const answersMap = {};
    previousAnswers.forEach((ans, index) => {
      const qItem =
        parsedQuestionsList.find((item) => {
          // 1. Try matching by question text
          const itemQuestion = getQuestionText(item);
          const ansQuestion = ans.question || "";
          if (
            itemQuestion &&
            ansQuestion &&
            itemQuestion.trim().toLowerCase() ===
              ansQuestion.trim().toLowerCase()
          ) {
            return true;
          }
          // 2. Try matching by sequence
          if (
            item.sequence !== undefined &&
            item.sequence !== null &&
            ans.sequence !== undefined &&
            ans.sequence !== null &&
            String(item.sequence) === String(ans.sequence)
          ) {
            return true;
          }
          return false;
        }) || parsedQuestionsList[index]; // 3. Fallback to index

      if (qItem) {
        const type = (qItem.type || "").toLowerCase().trim();
        const questionId = getQuestionId(qItem);

        let userAns = ans.user_answer;
        // Normalize True/False answers to A/B to match the options rendered
        if (type === "trueorfalse" || type === "truefalse") {
          if (
            userAns === true ||
            String(userAns).toLowerCase() === "true" ||
            String(userAns).toLowerCase() === "a"
          ) {
            userAns = "A";
          } else if (
            userAns === false ||
            String(userAns).toLowerCase() === "false" ||
            String(userAns).toLowerCase() === "b"
          ) {
            userAns = "B";
          }
        }

        if (type === "fillups") {
          answersMap[questionId] = { value: userAns, isSubmitted: true };
        } else if (type === "match") {
          try {
            answersMap[questionId] =
              typeof userAns === "string" ? JSON.parse(userAns) : userAns;
          } catch (e) {
            answersMap[questionId] = userAns;
          }
        } else {
          answersMap[questionId] = userAns;
        }
      }
    });
    return answersMap;
  };

  // Reactively update selected answers based on current selectedAttemptIndex
  useEffect(() => {
    if (isOpen) {
      if (effectiveViewMode || submitted) {
        // Load answers for the selected attempt
        const correctAnswers = {};
        let previousAnswers = currentAttemptObj?.user_answers || [];

        if (previousAnswers.length === 0) {
          const fallbackHistory =
            quizInfo.question_attempt_history ||
            quizInfo.history ||
            quizInfo.attempts ||
            [];
          if (fallbackHistory.length > 0) {
            // Sort fallback history by date descending (latest first)
            const sortedFallback = [...fallbackHistory]
              .reverse()
              .sort((a, b) => {
                const dateA = new Date(
                  a.attempted_at || a.created_at || a.last_attempted_at || 0,
                );
                const dateB = new Date(
                  b.attempted_at || b.created_at || b.last_attempted_at || 0,
                );
                const timeA = dateA.getTime();
                const timeB = dateB.getTime();
                if (isNaN(timeA) || isNaN(timeB)) return 0;
                return timeB - timeA;
              });
            previousAnswers =
              sortedFallback[selectedAttemptIndex]?.user_answers ||
              sortedFallback[0]?.user_answers ||
              [];
          }
        }

        if (previousAnswers.length > 0) {
          previousAnswers.forEach((ans, index) => {
            const qItem =
              parsedQuestionsList.find((item) => {
                // 1. Try matching by question text
                const itemQuestion = getQuestionText(item);
                const ansQuestion = ans.question || "";
                if (
                  itemQuestion &&
                  ansQuestion &&
                  itemQuestion.trim().toLowerCase() ===
                    ansQuestion.trim().toLowerCase()
                ) {
                  return true;
                }
                // 2. Try matching by sequence
                if (
                  item.sequence !== undefined &&
                  item.sequence !== null &&
                  ans.sequence !== undefined &&
                  ans.sequence !== null &&
                  String(item.sequence) === String(ans.sequence)
                ) {
                  return true;
                }
                return false;
              }) || parsedQuestionsList[index]; // 3. Fallback to index if no match found

            if (qItem) {
              const type = (qItem.type || "").toLowerCase().trim();
              const questionId = getQuestionId(qItem);

              let userAns = ans.user_answer;
              // Normalize True/False answers to A/B to match the options rendered
              if (type === "trueorfalse" || type === "truefalse") {
                if (
                  userAns === true ||
                  String(userAns).toLowerCase() === "true" ||
                  String(userAns).toLowerCase() === "a"
                ) {
                  userAns = "A";
                } else if (
                  userAns === false ||
                  String(userAns).toLowerCase() === "false" ||
                  String(userAns).toLowerCase() === "b"
                ) {
                  userAns = "B";
                }
              }

              if (type === "fillups") {
                correctAnswers[questionId] = {
                  value: userAns,
                  isSubmitted: true,
                };
              } else if (type === "match") {
                try {
                  correctAnswers[questionId] =
                    typeof userAns === "string" ? JSON.parse(userAns) : userAns;
                } catch (e) {
                  correctAnswers[questionId] = userAns;
                }
              } else {
                correctAnswers[questionId] = userAns;
              }
            }
          });
        } else {
          // Fallback to correct answers
          parsedQuestionsList.forEach((qItem) => {
            const questionId = getQuestionId(qItem);
            if (qItem.individualQuestion) {
              const correctAnswer =
                qItem.individualQuestion.correct_answer ||
                qItem.individualQuestion.answer ||
                "";
              correctAnswers[questionId] = correctAnswer;
            } else {
              let content = qItem.content || qItem;
              try {
                if (typeof content === "string") content = JSON.parse(content);
                if (typeof content === "string") content = JSON.parse(content);
              } catch (e) {}
              const type = (qItem.type || "").toLowerCase().trim();
              const questionObj =
                type === "match" ? content : content?.questions?.[0] || content;
              if (questionObj) {
                const correctAnswer =
                  questionObj.correct_answer || questionObj.answer || "";
                if (type === "match") {
                  correctAnswers[questionId] =
                    questionObj.pairs || questionObj.questions || [];
                } else if (type === "fillups") {
                  correctAnswers[questionId] = {
                    value: correctAnswer,
                    isSubmitted: true,
                  };
                } else {
                  correctAnswers[questionId] = correctAnswer;
                }
              }
            }
          });
        }
        setSelected(correctAnswers);
      } else {
        // Attempt mode: start with empty answers so the user can mark them from scratch.
        setSelected({});
      }
    }
  }, [
    isOpen,
    effectiveViewMode,
    submitted,
    selectedAttemptIndex,
    questions,
    contentQuizData,
    parsedQuestionsList,
  ]);

  // Reset selectedAttemptIndex and tabs when opening
  useEffect(() => {
    if (isOpen) {
      setCurrent(0);
      setSubmitted(false);
      setSubmitResult(null); 
      setSelectedAttemptIndex(0);
      if (effectiveViewMode) {
        setActiveTab("performance");
      } else {
        setActiveTab("questions");
      }
    }
  }, [isOpen, effectiveViewMode]);

  // Reset question index when switching between history attempts in review mode
  useEffect(() => {
    if (effectiveViewMode || submitted) {
      setCurrent(0);
    }
  }, [selectedAttemptIndex, effectiveViewMode, submitted]);


  if (!isOpen || !parsedQuestionsList || parsedQuestionsList.length === 0)
    return null;

  const q = parsedQuestionsList[current];
  if (!q) return null;
  const isLast = current === totalQ - 1;
  const isFirst = current === 0;

  const calculateScore = () => {
    let calculatedScore = 0;
    parsedQuestionsList.forEach((qItem) => {
      const questionId = getQuestionId(qItem);
      const userAnswerRaw = selected[questionId];
      if (userAnswerRaw === undefined || userAnswerRaw === null) return;

      const userAnswer =
        userAnswerRaw &&
        typeof userAnswerRaw === "object" &&
        "value" in userAnswerRaw
          ? userAnswerRaw.value
          : userAnswerRaw;

      let isCorrect = false;
      let correctAnswer = "";

      if (qItem.individualQuestion) {
        correctAnswer =
          qItem.individualQuestion.correct_answer ||
          qItem.individualQuestion.answer ||
          "";
      } else {
        let content = qItem.content || qItem;
        try {
          if (typeof content === "string") content = JSON.parse(content);
          if (typeof content === "string") content = JSON.parse(content);
        } catch (e) {
          return;
        }
        const type = (qItem.type || "").toLowerCase().trim();
        const questionObj =
          type === "match" ? content : content?.questions?.[0] || content;
        if (!questionObj) return;
        correctAnswer = questionObj.correct_answer || questionObj.answer || "";
      }

      const type = (qItem.type || "").toLowerCase().trim();

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        let normUser = String(userAnswer).trim().toLowerCase();
        let normCorrect = String(correctAnswer).trim().toLowerCase();

        if (type === "trueorfalse" || type === "truefalse") {
          if (normUser === "true") normUser = "a";
          else if (normUser === "false") normUser = "b";
          if (normCorrect === "true") normCorrect = "a";
          else if (normCorrect === "false") normCorrect = "b";
        }
        isCorrect = normUser === normCorrect;
      } else if (type === "fillups") {
        const normUser = String(userAnswer).trim().toLowerCase();
        const normCorrect = String(correctAnswer).trim().toLowerCase();
        isCorrect = normUser !== "" && normUser === normCorrect;
      } else if (type === "match") {
        let content = qItem.content || qItem;
        try {
          if (typeof content === "string") content = JSON.parse(content);
          if (typeof content === "string") content = JSON.parse(content);
        } catch (e) {}
        const questionObj = content;
        const correctPairs = questionObj.pairs || questionObj.questions || [];
        if (
          Array.isArray(userAnswer) &&
          userAnswer.length >= 2 &&
          Array.isArray(correctPairs) &&
          correctPairs.length >= 2
        ) {
          isCorrect =
            JSON.stringify(userAnswer[1].values) ===
            JSON.stringify(correctPairs[1].values);
        }
      }

      if (isCorrect) {
        calculatedScore++;
      }
    });
    return calculatedScore;
  };

  const displayScore =
    currentAttemptScore !== null ? currentAttemptScore : calculateScore();
  const latestPercentage =
    totalQ > 0 ? Math.round((displayScore / totalQ) * 100) : 0;

  const defaultFeedback = {
    title:
      latestPercentage >= 75
        ? "Excellent Vocabulary Skills!"
        : latestPercentage >= 50
          ? "Good Progress!"
          : "Keep Practising!",
    performance_level:
      latestPercentage >= 75
        ? "Excellent"
        : latestPercentage >= 50
          ? "Good"
          : "Needs Improvement",
    improvement_advice:
      latestPercentage >= 75
        ? "Great job! Keep up the excellent work and continue to challenge yourself with more advanced content."
        : latestPercentage >= 50
          ? "You are doing well. Focus on reviewing the questions you missed and practice regularly to improve further."
          : "Don't worry, practice makes perfect. Spend more time understanding the core concepts and try attempting the quiz again.",
    weak_topics: [],
  };

  // Get feedback for current active attempt
  let latestAttemptFeedback = null;
  if (
    currentAttemptObj?.feedback &&
    (!Array.isArray(currentAttemptObj.feedback) ||
      currentAttemptObj.feedback.length > 0)
  ) {
    const fb = currentAttemptObj.feedback;
    latestAttemptFeedback = Array.isArray(fb) ? fb[0] : fb;
  } else if (feedbackList.length > 0 && selectedAttemptIndex === 0) {
    latestAttemptFeedback = feedbackList[0];
  }

  if (!latestAttemptFeedback) {
    latestAttemptFeedback = defaultFeedback;
  }

  const getFeedbackBadgeClass = (perfLevel) => {
    const norm = (perfLevel || "").toLowerCase();
    if (norm.includes("excellent")) return "excellent";
    if (norm.includes("good")) return "good";
    if (norm.includes("average")) return "average";
    if (
      norm.includes("improve") ||
      norm.includes("poor") ||
      norm.includes("fail") ||
      norm.includes("practice") ||
      norm.includes("need")
    ) {
      return "needs-improvement";
    }
    return "default";
  };

  // Remaining attempts (all except the latest one) in chronological order
  const remainingAttempts = hasHistory ? sortedHistory.slice(1).reverse() : [];

  const getHistoryScore = (item) => {
    const correct =
      item.no_of_question_correct !== undefined
        ? item.no_of_question_correct
        : item.score !== undefined
          ? item.score
          : 0;
    const total =
      item.total_questions || item.no_of_questions_attempted || totalQ;
    return { correct, total };
  };

  const handleSubmit = async () => {
    if (!isAllQuestionsAnswered) return;
    const scoreVal = calculateScore();
    const user_answers = parsedQuestionsList.map((qItem) => {
      const questionId = getQuestionId(qItem);
      const userAnswerRaw = selected[questionId];

      const user_answer =
        userAnswerRaw &&
        typeof userAnswerRaw === "object" &&
        "value" in userAnswerRaw
          ? userAnswerRaw.value
          : userAnswerRaw;

      let correctAnswer = "";
      if (qItem.individualQuestion) {
        correctAnswer =
          qItem.individualQuestion.correct_answer ||
          qItem.individualQuestion.answer ||
          "";
      } else {
        let content = qItem.content || qItem;
        try {
          if (typeof content === "string") content = JSON.parse(content);
          if (typeof content === "string") content = JSON.parse(content);
        } catch (e) {}
        const type = (qItem.type || "").toLowerCase().trim();
        const questionObj =
          type === "match" ? content : content?.questions?.[0] || content;
        correctAnswer =
          questionObj?.correct_answer || questionObj?.answer || "";
      }

      const type = (qItem.type || "").toLowerCase().trim();
      let isCorrect = false;
      if (
        user_answer !== undefined &&
        user_answer !== null &&
        user_answer !== ""
      ) {
        if (
          type === "mcq" ||
          type === "quiz" ||
          type === "trueorfalse" ||
          type === "truefalse"
        ) {
          let normUser = String(user_answer).trim().toLowerCase();
          let normCorrect = String(correctAnswer).trim().toLowerCase();

          if (type === "trueorfalse" || type === "truefalse") {
            if (normUser === "true") normUser = "a";
            else if (normUser === "false") normUser = "b";
            if (normCorrect === "true") normCorrect = "a";
            else if (normCorrect === "false") normCorrect = "b";
          }
          isCorrect = normUser === normCorrect;
        } else if (type === "fillups") {
          const normUser = String(user_answer).trim().toLowerCase();
          const normCorrect = String(correctAnswer).trim().toLowerCase();
          isCorrect = normUser !== "" && normUser === normCorrect;
        } else if (type === "match") {
          let content = qItem.content || qItem;
          try {
            if (typeof content === "string") content = JSON.parse(content);
            if (typeof content === "string") content = JSON.parse(content);
          } catch (e) {}
          const questionObj = content;
          const correctPairs = questionObj.pairs || questionObj.questions || [];
          if (
            Array.isArray(user_answer) &&
            user_answer.length >= 2 &&
            Array.isArray(correctPairs) &&
            correctPairs.length >= 2
          ) {
            isCorrect =
              JSON.stringify(user_answer[1].values) ===
              JSON.stringify(correctPairs[1].values);
          }
        }
      }

      const origQuestionText = getQuestionText(qItem);

      return {
        question: origQuestionText,
        sequence: qItem.sequence,
        user_answer: user_answer || "",
        correct_answer: correctAnswer,
        is_correct: isCorrect,
      };
    });

    const attemptedCount = parsedQuestionsList.filter((qItem) => {
      const qId = getQuestionId(qItem);
      return selected[qId] !== undefined && selected[qId] !== null;
    }).length;

    const payload = {
      no_of_questions_attempted: attemptedCount,
      no_of_question_correct: scoreVal,
      user_answers: user_answers,
    };

    try {
      setSubmitting(true);
      const res = await submitContentQuizApi(pageTitle, pageKey, activeId, payload);
      
      const isPassed = res ? res.is_passed : (scoreVal / totalQ * 100 >= 80);
      setSubmitResult({
        isPassed: isPassed,
        score: scoreVal,
        total: totalQ,
      });
      setSubmitted(true);
      
      if (refreshContentQuizData) {
        refreshContentQuizData(activeId);
      }
      if (onQuizSubmitSuccess) {
        onQuizSubmitSuccess();
      }
    } catch (error) {
      console.error("Error submitting content quiz:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReattempt = () => {
    setSelected({});
    setCurrent(0);
    setSubmitted(false);
    setSubmitResult(null);
    setActiveTab("questions");
    if (refreshContentQuizData) {
      refreshContentQuizData(activeId);
    }
    if (refreshQuizEval) {
      refreshQuizEval(true);
    }
    if (pollQuizEval) {
      const targetQuizId = activeId || contentQuizData?.[0]?.id;
      if (targetQuizId) pollQuizEval(targetQuizId);
    }
  };


  const renderSubmitResultScreen = () => {
    if (!submitResult) return null;
    const { isPassed, score, total } = submitResult;
    const pct = total > 0 ? Math.round((score / total) * 100) : 0;
    
    return (
      <div className="cqp-result-screen">
        <div className={`cqp-result-card`}>
          <div className="cqp-result-icon-wrapper">
            {isPassed ? (
              <div className="cqp-success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="cqp-fail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            )}
          </div>

          <h2 className="cqp-result-title">
            {isPassed ? "Success! You Passed" : "Quiz Not Passed"}
          </h2>

          <div className="cqp-result-score-container">
            <div className="cqp-result-score-main">
              <span className="score-num">{score}</span>
              <span className="score-slash">/</span>
              <span className="score-total">{total}</span>
            </div>
            <div className="cqp-result-percentage">{pct}% Correct</div>
          </div>

          {/* <p className="cqp-result-description">
            {isPassed
              ? "Congratulations! You have met the passing criteria and successfully completed this section quiz."
              : "You need a score of 80% or higher to pass. Don't worry—practice makes perfect. The questions have been regenerated for you to try again!"}
          </p> */}

          <div className="cqp-result-actions">
            {!isPassed ? (
              <button className="cqp-result-btn reattempt-btn" onClick={handleReattempt}>
                 Reattempt Questions
              </button>
            ) : (
              <button
                className="cqp-result-btn review-btn"
                onClick={() => {
                  setSubmitResult(null);
                  setActiveTab("questions");
                }}
              >
                 Review Answers
              </button>
            )}
            <button className="cqp-result-btn close-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAttemptSelector = () => {
    if (!viewMode && !submitted) return null;

    const options = [];
    if (hasHistory) {
      sortedHistory.forEach((item, idx) => {
        const { correct, total } = getHistoryScore(item);
        const attemptNum = sortedHistory.length - idx;
        options.push({
          index: idx,
          label: `Attempt ${attemptNum} (${correct}/${total})`,
        });
      });
    } else {
      options.push({
        index: 0,
        label: `Attempt #1 (${displayScore}/${totalQ})`,
      });
    }

    return (
      <div className="cqp-attempt-selector">
        <div className="cqp-attempt-selector-left">
          <label htmlFor="attempt-select">Viewing Attempt:</label>
          <select
            id="attempt-select"
            value={selectedAttemptIndex}
            onChange={(e) => setSelectedAttemptIndex(Number(e.target.value))}
          >
            {options.map((opt) => (
              <option key={opt.index} value={opt.index}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <button
          className="cqp-toggle-feedback-btn"
          onClick={() => setShowFeedbackBox(!showFeedbackBox)}
        >
          💡 {showFeedbackBox ? "Hide Feedback" : "Show Feedback"}
        </button>
      </div>
    );
  };

  const renderPerformanceTab = () => {
    return (
      <div className="cqp-performance-container">
        {/* Stats Cards */}
        <div className="cqp-stats-grid">
          <div className="cqp-stat-card">
            <span className="cqp-stat-label">Total Attempts</span>
            <span className="cqp-stat-value">{noOfAttempts}</span>
          </div>
          <div className="cqp-stat-card">
            <span className="cqp-stat-label">Latest Score</span>
            <span className="cqp-stat-value">
              {displayScore} / {totalQ}
            </span>
            <span className="cqp-stat-sub">({latestPercentage}%)</span>
          </div>
        </div>

        {/* Latest Feedback Card (Last test show first) */}
        <div className="cqp-feedback-section">
          <h3>Latest Feedback</h3>
          <div
            className={`cqp-feedback-card ${latestAttemptFeedback.performance_level?.toLowerCase().replace(/\s+/g, "-") || "average"}`}
          >
            <div className="cqp-feedback-header">
              <span className="cqp-feedback-badge">
                {latestAttemptFeedback.performance_level}
              </span>
              <h4 className="cqp-feedback-title">
                {latestAttemptFeedback.title}
              </h4>
            </div>

            {latestAttemptFeedback.weak_topics &&
              latestAttemptFeedback.weak_topics.length > 0 && (
                <div className="cqp-weak-topics-box">
                  <span className="cqp-weak-label">Weak Areas:</span>
                  <div className="cqp-weak-tags">
                    {latestAttemptFeedback.weak_topics.map((topic, i) => (
                      <span key={i} className="cqp-weak-tag">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            <p className="cqp-feedback-advice">
              {latestAttemptFeedback.improvement_advice}
            </p>



            <button
              className="cqp-review-attempt-btn"
              onClick={() => {
                setSelectedAttemptIndex(0);
                setActiveTab("questions");
              }}
            >
              🔍 Review Latest Attempt
            </button>
          </div>
        </div>

        {/* Attempt History List (Remaining attempts shown in cards) */}
        {remainingAttempts.length > 0 && (
          <div className="cqp-history-section">
            <h3>Attempt History</h3>
            <div className="cqp-history-list">
              {remainingAttempts.map((item, idx) => {
                const itemFeedback = Array.isArray(item.feedback)
                  ? item.feedback[0]
                  : item.feedback || null;

                const attemptNum = idx + 1;

                return (
                  <div key={idx} className="cqp-history-card">
                    <div className="cqp-history-card-body">
                      <div className="cqp-history-card-left">
                        <span className="cqp-history-num">
                          Attempt #{attemptNum}
                        </span>
                        {itemFeedback?.performance_level && (
                          <span
                            className={`cqp-history-perf-badge ${getFeedbackBadgeClass(itemFeedback.performance_level)}`}
                          >
                            {itemFeedback.performance_level}
                          </span>
                        )}
                      </div>
                      <button
                        className="cqp-review-attempt-btn-small"
                        onClick={() => {
                          setSelectedAttemptIndex(
                            sortedHistory.length - 1 - idx,
                          );
                          setActiveTab("questions");
                        }}
                      >
                        📝 View Report
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuestionComponent = () => {
    if (!q) return null;
    let content = q.content || q;
    try {
      if (typeof content === "string") content = JSON.parse(content);
      if (typeof content === "string") content = JSON.parse(content);
    } catch (e) {
      console.error("Error parsing content:", e);
    }

    const type = (q.type || "").toLowerCase().trim();
    const questionIdForComponent = getQuestionId(q);
    const componentMode = effectiveViewMode || submitted ? "review" : "test";


    // Prepare questions list for child component
    const targetQuestion = q.individualQuestion || content;

    switch (type) {
      case "match":
        return (
          <Match
            key={questionIdForComponent}
            data={content}
            mode={componentMode}
            answers={selected}
            setAnswers={setSelected}
            questionId={questionIdForComponent}
            retest={false}
            isAttended={false}
          />
        );
      case "trueorfalse":
      case "truefalse":
        return (
          <TrueFalse
            key={questionIdForComponent}
            questions={[targetQuestion]}
            answers={selected}
            setAnswers={setSelected}
            mode={componentMode}
            questionId={questionIdForComponent}
          />
        );
      case "fillups":
        return (
          <Fillups
            key={questionIdForComponent}
            questions={[targetQuestion]}
            current={0}
            answers={selected}
            setAnswers={setSelected}
            mode={componentMode}
            questionId={questionIdForComponent}
          />
        );
      case "mcq":
      case "quiz":
      default:
        return (
          <MCQ
            key={questionIdForComponent}
            questions={[targetQuestion]}
            current={0}
            answers={selected}
            setAnswers={setSelected}
            mode={componentMode}
            questionId={questionIdForComponent}
          />
        );
    }
  };

  return (
    <>
      <div
        className="cqp-overlay"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {(submitting || quizRegenerating) && <GlobalLoader />}
        <div className="cqp-modal">
          {/* Header */}
          <div className="cqp-header">
            <div className="cqp-header-left">
              <span className="cqp-badge">
                {submitResult
                  ? "Quiz Result"
                  : effectiveViewMode
                    ? "View Answers"
                    : "Attempt Test"}
              </span>
              {isAiEvalEnabled && !isAdmin && (
                <>
                  {quizRegenerating || isEvaluatingEval ? (
                    <button
                      type="button"
                      className="ps-row__aieval-chip evaluated loading"
                      style={{ marginLeft: "12px", border: "none", cursor: "default" }}
                    >
                      Evaluating...
                    </button>
                  ) : (
                    parsedEval.overall !== null && (
                      <button
                        type="button"
                        className="ps-row__aieval-chip evaluated"
                        style={{ marginLeft: "12px", border: "none", cursor: "pointer" }}
                        onClick={() => setIsEvalBreakdownOpen(true)}
                      >
                        Eval: {parsedEval.overall}/10
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className={`ps-row__aieval-chip regenerate ${quizRegenerating || isEvaluatingEval ? "loading" : ""}`}
                    style={{ marginLeft: "8px", cursor: "pointer" }}
                    onClick={handleRegenerateQuiz}
                    disabled={quizRegenerating || isEvaluatingEval || submitting}
                    title="Regenerate Personalized Quiz Questions with AI"
                  >
                    {quizRegenerating ? "Regenerating..." : "Regenerate Quiz"}
                  </button>
                </>
              )}



              {activeTab === "questions" && !submitResult && (
                <span className="cqp-progress">
                  Q{current + 1} of {totalQ}
                </span>
              )}
            </div>
          <button className="cqp-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Progress bar */}
        {activeTab === "questions" && !submitResult && (
          <div className="cqp-progress-bar">
            <div
              className="cqp-progress-fill"
              style={{ width: `${((current + 1) / totalQ) * 100}%` }}
            />
          </div>
        )}

        {/* Tabs - visible only when effectiveViewMode or submitted is true AND submitResult is null */}
        {(effectiveViewMode || (submitted && !submitResult)) && (
          <div className="cqp-tabs">
            <button
              className={`cqp-tab-btn ${activeTab === "performance" ? "active" : ""}`}
              onClick={() => setActiveTab("performance")}
            >
              📊 Performance & History
            </button>
            <button
              className={`cqp-tab-btn ${activeTab === "questions" ? "active" : ""}`}
              onClick={() => setActiveTab("questions")}
            >
              📝 Review Questions
            </button>
          </div>
        )}

        {/* Attempt Selector - fixed at the top when reviewing questions */}
        {activeTab === "questions" && !submitResult && renderAttemptSelector()}

        {/* Result banner shown after submit */}
        {(submitted || effectiveViewMode) &&
          !submitResult &&
          activeTab === "questions" &&
          current === totalQ - 1}

        {/* Modal Body */}
        <div className="cqp-body">
          {/* Feedback box for the selected attempt when reviewing questions */}
          {activeTab === "questions" &&
            (submitted || effectiveViewMode) &&
            !submitResult &&
            showFeedbackBox &&
            latestAttemptFeedback && (
              <div
                className={`cqp-review-feedback-box ${getFeedbackBadgeClass(latestAttemptFeedback.performance_level)}`}
              >
                <div className="cqp-review-feedback-header">
                  {latestAttemptFeedback.performance_level && (
                    <div
                      className={`performance-badge ${getFeedbackBadgeClass(latestAttemptFeedback.performance_level)}`}
                    >
                      {latestAttemptFeedback.performance_level}
                    </div>
                  )}
                  {latestAttemptFeedback.title && (
                    <span className="cqp-review-feedback-title">
                      💡 {latestAttemptFeedback.title}
                    </span>
                  )}
                </div>
                {latestAttemptFeedback.improvement_advice && (
                  <p className="cqp-review-feedback-advice">
                    {latestAttemptFeedback.improvement_advice}
                  </p>
                )}
                {latestAttemptFeedback.weak_topics &&
                  latestAttemptFeedback.weak_topics.length > 0 && (
                    <div className="cqp-review-weak-topics">
                      <span className="cqp-review-weak-label">
                        Areas to Focus On:
                      </span>
                      <div className="cqp-review-weak-tags">
                        {latestAttemptFeedback.weak_topics.map((topic, i) => (
                          <span key={i} className="cqp-review-weak-tag">
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          {submitResult
            ? renderSubmitResultScreen()
            : activeTab === "performance"
              ? renderPerformanceTab()
              : renderQuestionComponent()}
        </div>

        {/* Footer navigation */}
        {!submitResult && (
          <div className="cqp-footer">
            {activeTab === "performance" ? (
              <>
                <div className="cqp-footer-center"></div>
                <button className="cqp-nav-btn close-btn-foot" onClick={onClose}>
                  Close
                </button>
              </>
            ) : (
              <>
                <button
                  className="cqp-nav-btn prev"
                  disabled={isFirst}
                  onClick={() => setCurrent((c) => c - 1)}
                >
                  Previous
                </button>

                <div className="cqp-footer-center"></div>

                {isLast ? (
                  !submitted && !effectiveViewMode ? (
                    <button

                      className="cqp-nav-btn submit"
                      disabled={submitting || !isAllQuestionsAnswered}
                      onClick={handleSubmit}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>
                  ) : (
                    <button
                      className="cqp-nav-btn close-btn-foot"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  )
                ) : (
                  <button
                    className="cqp-nav-btn next"
                    onClick={() => setCurrent((c) => c + 1)}
                  >
                    Next
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
    <AiEvalBreakdownPopup
      open={isEvalBreakdownOpen}
      onClose={() => setIsEvalBreakdownOpen(false)}
      evalData={aiEvalData}
    />
  </>
);
};

export default ContentQuizPopup;
